import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Pencil, Loader2, X, UserCheck, KeyRound, Clock, Mail, Chrome } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [resetReqs, setResetReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/users"),
      api.get("/users/pending"),
      api.get("/users/reset-requests"),
      api.get("/settings"),
    ]).then(([u, p, r, s]) => {
      setUsers(u.data); setPending(p.data); setResetReqs(r.data);
      setGoogleEnabled(!!s.data.google_login_enabled);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleGoogle = async (val) => {
    setGoogleEnabled(val);
    try {
      await api.put("/settings", { google_login_enabled: val });
      toast.success(val ? "Google sign-in enabled" : "Google sign-in disabled");
    } catch (e) {
      setGoogleEnabled(!val);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Member added");
      setShowAdd(false);
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const approve = async (u, role) => {
    try {
      await api.post(`/users/${u.id}/approve`, { role });
      toast.success(`${u.name} approved as ${role}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReset = (target) => { setResetTarget(target); setNewPassword(""); };

  const openResetByEmail = (email) => {
    const u = users.find((x) => x.email === email);
    if (!u) { toast.error("This account uses Google sign-in (no password to reset)."); return; }
    openReset(u);
  };

  const submitReset = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading)
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="min-h-screen p-10">
      <div className="rf-fade-up flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Access Control</p>
          <h1 className="mt-1 font-heading text-4xl font-bold">Team</h1>
          <p className="mt-2 text-gray-400">Invite-only. Approve requests and manage roles & passwords.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-member-btn" className="flex items-center gap-2 rounded-md bg-[#00E5FF] px-4 py-2.5 text-sm font-semibold text-black hover:bg-[#33EBFF]">
          <UserPlus className="h-4 w-4" /> Add Member
        </button>
      </div>

      {/* Sign-in settings */}
      <div className="mt-8 flex items-center justify-between rounded-lg border border-white/10 bg-[#121212] p-5" data-testid="settings-section">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#1A1A1A] text-[#00E5FF]">
            <Chrome className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold">Google Sign-in</h3>
            <p className="mt-0.5 max-w-lg text-sm text-gray-400">
              Let team members request access with Google (still needs your approval). Off by default — turn on only when you want to allow it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold uppercase tracking-wide ${googleEnabled ? "text-[#34C759]" : "text-gray-500"}`}>
            {googleEnabled ? "On" : "Off"}
          </span>
          <Switch checked={googleEnabled} onCheckedChange={toggleGoogle} data-testid="google-login-toggle" />
        </div>
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="mt-8 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-5" data-testid="pending-section">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-[#00E5FF]">
            <UserCheck className="h-5 w-5" /> Pending Approvals ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} data-testid={`pending-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/10 bg-[#0f0f0f] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A1A1A] text-sm font-semibold text-[#00E5FF]">{u.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="flex items-center gap-1 text-xs text-gray-500"><Chrome className="h-3 w-3" /> {u.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => approve(u, "editor")} data-testid={`approve-editor-${u.id}`} className="rounded-md bg-[#00E5FF] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#33EBFF]">Approve as Editor</button>
                  <button onClick={() => approve(u, "admin")} data-testid={`approve-admin-${u.id}`} className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-medium hover:bg-white/5">Make Admin</button>
                  <button onClick={() => remove(u)} data-testid={`reject-${u.id}`} className="rounded-md p-1.5 text-[#FF3B30] hover:bg-white/5"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset requests */}
      {resetReqs.length > 0 && (
        <div className="mt-6 rounded-lg border border-[#FF007F]/30 bg-[#FF007F]/5 p-5" data-testid="reset-requests-section">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-[#FF007F]">
            <KeyRound className="h-5 w-5" /> Password Reset Requests ({resetReqs.length})
          </h3>
          <div className="space-y-2">
            {resetReqs.map((r) => (
              <div key={r.id} data-testid={`reset-req-${r.id}`} className="flex items-center justify-between rounded-md border border-white/10 bg-[#0f0f0f] px-4 py-3">
                <div>
                  <div className="font-medium">{r.name || r.email}</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500"><Mail className="h-3 w-3" /> {r.email} · <Clock className="h-3 w-3" /> requested</div>
                </div>
                <button onClick={() => openResetByEmail(r.email)} data-testid={`resolve-reset-${r.id}`} className="rounded-md bg-[#FF007F] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#ff3399]">Set New Password</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members table */}
      <div className="mt-6 overflow-hidden rounded-lg border border-white/10 bg-[#121212]">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-5 py-3 font-medium">Member</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Sign-in</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5 last:border-0">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1A1A1A] text-xs font-semibold text-[#00E5FF]">{u.name?.[0]?.toUpperCase()}</div>
                    <span className="font-medium">{u.name}{u.id === user.id && <span className="ml-1.5 text-xs text-gray-500">(you)</span>}</span>
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-400">{u.email}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                    {u.auth_provider === "google" ? <><Chrome className="h-3 w-3" /> Google</> : <><Mail className="h-3 w-3" /> Password</>}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${u.role === "admin" ? "border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F]"}`}>
                    {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <Pencil className="h-3 w-3" />} {u.role}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openReset(u)} data-testid={`reset-pw-${u.id}`} title="Reset password" className="rounded-md p-2 text-[#FF007F] hover:bg-white/5"><KeyRound className="h-4 w-4" /></button>
                    {u.id !== user.id && (
                      <button onClick={() => remove(u)} data-testid={`remove-user-${u.id}`} title="Remove" className="rounded-md p-2 text-[#FF3B30] hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add member modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="add-member-modal">
          <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#121212] p-6">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-xl font-semibold">Add Team Member</h3>
              <button onClick={() => setShowAdd(false)} data-testid="close-modal-btn" className="rounded-md p-1 text-gray-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <MField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="member-name" />
              <MField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="member-email" />
              <MField label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="member-password" />
              <div>
                <label className="mb-1.5 block text-sm text-gray-400">Role</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["editor", "Editor"], ["admin", "Admin"]].map(([v, l]) => (
                    <button key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`role-${v}`}
                      className={`rounded-md border py-2 text-sm font-medium ${form.role === v ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <button onClick={create} disabled={saving} data-testid="submit-member-btn" className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#00E5FF] font-semibold text-black hover:bg-[#33EBFF] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="reset-password-modal">
          <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#121212] p-6">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-xl font-semibold">Reset Password</h3>
              <button onClick={() => setResetTarget(null)} data-testid="close-reset-btn" className="rounded-md p-1 text-gray-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-4 text-sm text-gray-400">Set a new password for <span className="font-medium text-white">{resetTarget.name}</span> ({resetTarget.email}). Share it with them securely.</p>
            <MField label="New password" type="text" value={newPassword} onChange={setNewPassword} testId="new-password-input" />
            <button onClick={submitReset} disabled={saving} data-testid="submit-reset-btn" className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#FF007F] font-semibold text-white hover:bg-[#ff3399] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MField({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm text-gray-400">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="h-10 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-3 text-sm text-white focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]" />
    </div>
  );
}
